// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — achats d'une campagne (DÉMONSTRATION) :
//   • fournisseurs (référentiel de l'organisation) et devis (pièce obligatoire, ajout seul) ;
//   • commandes : préparées et soumises (nombre minimal de devis du règlement et devis retenu motivé, OU dérogation motivée), validées ou
//     refusées par une AUTRE personne (numéro attribué à la validation), annulées avec motif avant toute réception ;
//   • acompte : PAIEMENT de la trésorerie adossé à la commande (préparé ici ; signature, contre-signature et exécution
//     sur la fiche du paiement, écran de la trésorerie) ; factures et avoirs (après la réception, pièce obligatoire).
// Routes : /api/dispositifs/campagnes/{fournisseurs, :id/devis, :id/commandes, commandes/:id/…, :id/achats}.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Banknote, Building2, CheckCircle2, FileText, MoreVertical, Plus, Send, ShoppingCart, Undo2, XCircle } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, MenuActions } from '../../design/composants';
import { toArray, aujourdhuiDakar } from './api';
import { Champ, Etat } from './commun';
import { useSocial, francs, dateLongue, lireMontant, ETATS_PAIEMENT } from './apiSocial';
import { ChoixCompteMode, choixInitial, lireChoix, paiementComplet, champsPaiement } from './ChoixPaiement';
import {
  useLectureSociale, EtatsLecture, FenetreActe, TitreSection, motifValide, ChampMotif, ChoixRadio, usePieceDepot, ChampPiece, BandeauComptaDemo, Montant,
} from './communSocial';
import { useCampagnes, quantiteUnites, STATUTS_COMMANDE } from './apiCampagnes';
import { codeEtat, lireEntier } from './communCampagnes';

const ETATS_ACHAT = ['PUBLIEE', 'OUVERTE', 'INSCRIPTIONS_CLOSES', 'EN_DISTRIBUTION'];

// ─── Fournisseurs ─────────────────────────────────────────────────────────────────────────────
function FenetreFournisseur({ organisationId, onFermer, onFait }) {
  const api = useCampagnes();
  const [raison, setRaison] = useState('');
  const [ninea, setNinea] = useState('');
  const [tel, setTel] = useState('');
  const [adresse, setAdresse] = useState('');
  return (
    <FenetreActe titre="Nouveau fournisseur" Icone={Building2} libelleBouton="Enregistrer" pret={raison.trim().length >= 2} onFermer={onFermer}
      onConfirmer={async () => {
        await api.creerFournisseur({ organisation_id: organisationId, raison_sociale: raison.trim(), ...(ninea.trim() ? { ninea: ninea.trim() } : {}),
          ...(tel.trim() ? { telephone: tel.trim() } : {}), ...(adresse.trim() ? { adresse: adresse.trim() } : {}) });
        onFait('Fournisseur enregistré.');
      }}>
      <Champ libelle="Raison sociale" requis><input className="rh-champ-saisie" maxLength={160} value={raison} onChange={(e) => setRaison(e.target.value)} /></Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Champ libelle="NINEA"><input className="rh-champ-saisie" maxLength={20} value={ninea} onChange={(e) => setNinea(e.target.value)} /></Champ>
        <Champ libelle="Téléphone"><input className="rh-champ-saisie" maxLength={30} value={tel} onChange={(e) => setTel(e.target.value)} /></Champ>
      </div>
      <Champ libelle="Adresse"><input className="rh-champ-saisie" maxLength={300} value={adresse} onChange={(e) => setAdresse(e.target.value)} /></Champ>
    </FenetreActe>
  );
}

// ─── Devis ────────────────────────────────────────────────────────────────────────────────────
function FenetreDevis({ campagne, fournisseurs, onFermer, onFait }) {
  const api = useCampagnes();
  const [fournisseur, setFournisseur] = useState('');
  const [quantite, setQuantite] = useState('');
  const [prix, setPrix] = useState('');
  const piece = usePieceDepot(campagne.organisation_id, 'CAMPAGNE', campagne.id, 'DEVIS');
  const q = lireEntier(quantite);
  const p = lireMontant(prix);
  return (
    <FenetreActe titre="Enregistrer un devis" Icone={FileText} libelleBouton="Enregistrer le devis" pret={!!(fournisseur && q && p && piece.prete)} onFermer={onFermer}
      aide="Un devis enregistré ne se modifie pas et ne se supprime pas."
      onConfirmer={async () => {
        const pc = await piece.deposer();
        await api.ajouterDevis(campagne.id, { fournisseur_id: fournisseur, quantite: q, prix_unitaire: p, piece_id: pc.id });
        onFait('Devis enregistré.');
      }}>
      <Champ libelle="Fournisseur" requis>
        <select className="rh-champ-saisie" value={fournisseur} onChange={(e) => setFournisseur(e.target.value)}>
          <option value="">Choisir…</option>
          {fournisseurs.filter((f) => f.actif !== false).map((f) => <option key={f.id} value={f.id}>{f.raison_sociale}</option>)}
        </select>
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <Champ libelle={`Quantité (${campagne.unite_pluriel})`} requis><input className="rh-champ-saisie" inputMode="numeric" value={quantite} onChange={(e) => setQuantite(e.target.value)} /></Champ>
        <Champ libelle="Prix unitaire (F)" requis aide={q && p ? `Montant : ${francs(q * p)}` : undefined}><input className="rh-champ-saisie" inputMode="numeric" value={prix} onChange={(e) => setPrix(e.target.value)} /></Champ>
      </div>
      <ChampPiece piece={piece} types={['DEVIS']} libelleChamp="Devis (pièce)" />
    </FenetreActe>
  );
}

// ─── Commandes ────────────────────────────────────────────────────────────────────────────────
/** « au moins N devis » : nombre lu sur le serveur (paramètre DS_CA_NB_DEVIS_MIN ou défaut décidé), jamais codé ici. */
const exigenceDevis = (min) => (min ? `${min} devis au moins` : 'le nombre de devis exigé par le règlement');

function FenetreCommande({ campagne, fournisseurs, devis, devisMinimum, onFermer, onFait }) {
  const api = useCampagnes();
  const [fournisseur, setFournisseur] = useState('');
  const [quantite, setQuantite] = useState('');
  const [prix, setPrix] = useState('');
  const [mode, setMode] = useState('DEVIS');
  const [devisId, setDevisId] = useState('');
  const [motifChoix, setMotifChoix] = useState('');
  const [motifSans, setMotifSans] = useState('');
  const [livraison, setLivraison] = useState('');
  const [reprise, setReprise] = useState('');
  const q = lireEntier(quantite);
  const p = lireMontant(prix);
  const devisDuFournisseur = devis.filter((d) => d.fournisseur_id === fournisseur);
  const justification = mode === 'DEVIS' ? (devisId && motifValide(motifChoix, 5)) : motifValide(motifSans, 10);
  return (
    <FenetreActe titre="Préparer une commande" Icone={ShoppingCart} libelleBouton="Préparer" pret={!!(fournisseur && q && p && justification)} large onFermer={onFermer}
      aide={`${devis.length} devis enregistré(s) pour cette campagne. La validation exige ${exigenceDevis(devisMinimum)} et le devis retenu avec le motif du choix, ou une dérogation motivée (fournisseur unique, urgence…). Le numéro de bon de commande est attribué à la validation, par une autre personne.`}
      onConfirmer={async () => {
        await api.preparerCommande(campagne.id, {
          fournisseur_id: fournisseur, quantite: q, prix_unitaire: p,
          ...(mode === 'DEVIS' ? { devis_id: devisId, motif_choix: motifChoix.trim() } : { motif_sans_devis: motifSans.trim() }),
          ...(livraison ? { date_livraison: livraison } : {}), ...(reprise.trim() ? { conditions_reprise: reprise.trim() } : {}),
        });
        onFait('Commande préparée en brouillon : soumettez-la pour validation.');
      }}>
      <Champ libelle="Fournisseur" requis>
        <select className="rh-champ-saisie" value={fournisseur} onChange={(e) => { setFournisseur(e.target.value); setDevisId(''); }}>
          <option value="">Choisir…</option>
          {fournisseurs.filter((f) => f.actif !== false).map((f) => <option key={f.id} value={f.id}>{f.raison_sociale}</option>)}
        </select>
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <Champ libelle={`Quantité (${campagne.unite_pluriel})`} requis><input className="rh-champ-saisie" inputMode="numeric" value={quantite} onChange={(e) => setQuantite(e.target.value)} /></Champ>
        <Champ libelle="Prix unitaire (F)" requis aide={q && p ? `Montant : ${francs(q * p)}` : undefined}><input className="rh-champ-saisie" inputMode="numeric" value={prix} onChange={(e) => setPrix(e.target.value)} /></Champ>
        <Champ libelle="Livraison prévue"><input type="date" className="rh-champ-saisie" value={livraison} onChange={(e) => setLivraison(e.target.value)} /></Champ>
      </div>
      <ChoixRadio nom="justification" valeur={mode} onChange={setMode}
        options={{ DEVIS: 'Devis retenu parmi les devis enregistrés', DEROGATION: devisMinimum ? `Dérogation motivée (sans les ${devisMinimum} devis)` : 'Dérogation motivée' }} />
      {mode === 'DEVIS' ? (
        <>
          <Champ libelle="Devis retenu" requis aide={fournisseur && !devisDuFournisseur.length ? 'Aucun devis de ce fournisseur : enregistrez-le d\'abord.' : undefined}>
            <select className="rh-champ-saisie" value={devisId} onChange={(e) => setDevisId(e.target.value)} disabled={!fournisseur}>
              <option value="">Choisir…</option>
              {devisDuFournisseur.map((d) => <option key={d.id} value={d.id}>{quantiteUnites(campagne, d.quantite)} à {francs(d.prix_unitaire)} — {francs(d.montant)}</option>)}
            </select>
          </Champ>
          <ChampMotif valeur={motifChoix} onChange={setMotifChoix} libelleChamp="Motif du choix" aide="5 caractères au moins." />
        </>
      ) : (
        <ChampMotif valeur={motifSans} onChange={setMotifSans} libelleChamp="Motif de la dérogation" aide="10 caractères au moins (fournisseur unique, urgence…)." />
      )}
      <Champ libelle="Conditions de reprise (facultatives)"><textarea className="rh-champ-saisie" rows={2} value={reprise} onChange={(e) => setReprise(e.target.value)} /></Champ>
    </FenetreActe>
  );
}

function FenetreDecision({ commande, valider, onFermer, onFait }) {
  const api = useCampagnes();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`${valider ? 'Valider' : 'Refuser'} la commande de ${francs(commande.montant)}`} Icone={valider ? CheckCircle2 : XCircle}
      libelleBouton={valider ? 'Valider' : 'Refuser'} danger={!valider} pret={valider ? (!motif.trim() || motifValide(motif, 3)) : motifValide(motif, 3)} onFermer={onFermer}
      aide={valider ? 'La personne qui a préparé la commande ne la valide pas (double regard). Le numéro de bon de commande est attribué maintenant ; la commande est ensuite figée.' : 'La commande revient en brouillon.'}
      onConfirmer={async (cle) => {
        await api.deciderCommande(commande.id, { decision: valider ? 'VALIDER' : 'REFUSER', ...(motif.trim() ? { motif: motif.trim() } : {}) }, cle);
        onFait(valider ? 'Commande validée.' : 'Commande refusée : elle revient en brouillon.');
      }}>
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>{commande.raison_sociale} — {commande.quantite} × {francs(commande.prix_unitaire)}{commande.motif_sans_devis ? ` · dérogation : « ${commande.motif_sans_devis} »` : commande.motif_choix ? ` · motif du choix : « ${commande.motif_choix} »` : ''}</p>
      <ChampMotif valeur={motif} onChange={setMotif} requis={!valider} libelleChamp={valider ? 'Observation (facultative)' : 'Motif du refus'} />
    </FenetreActe>
  );
}

function FenetreAnnulerCommande({ commande, onFermer, onFait }) {
  const api = useCampagnes();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre="Annuler la commande" Icone={Undo2} libelleBouton="Annuler la commande" danger pret={motifValide(motif, 5)} onFermer={onFermer}
      aide="Possible avant toute réception et sans acompte en cours ou exécuté (un acompte s'annule ou se fait rembourser d'abord, en trésorerie)."
      onConfirmer={async () => { await api.annulerCommande(commande.id, motif.trim()); onFait('Commande annulée.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

/** Acompte = paiement FINANCE adossé à la commande (DI-11) : préparé ici, puis signé, contresigné et exécuté sur sa fiche. */
function FenetreAcompte({ commande, organisationId, onFermer, onFait }) {
  const api = useCampagnes();
  const social = useSocial();
  const treso = useLectureSociale(() => social.tresorerie(organisationId), [social, organisationId]);
  const comptes = toArray(treso.donnees?.comptes).filter((c) => c.etat === 'ACTIF');
  const deja = toArray(commande.acomptes).filter((a) => a.statut !== 'ANNULE').reduce((s, a) => s + Number(a.montant || 0), 0);
  const reste = Number(commande.montant) - deja;
  const [choix, setChoix] = useState(choixInitial);
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(aujourdhuiDakar());
  const [reference, setReference] = useState('');
  const ch = lireChoix(comptes, choix);
  const m = lireMontant(montant);
  return (
    <FenetreActe titre={`Acompte sur la commande ${commande.numero}`} Icone={Banknote} libelleBouton="Préparer l'acompte" pret={!!(paiementComplet(ch) && m && m <= reste && date)} onFermer={onFermer}
      aide="L'acompte est un paiement de la trésorerie adossé à la commande. Il est ensuite signé, contresigné si le montant l'exige, puis exécuté avec sa pièce par d'autres personnes, depuis la fiche du paiement."
      onConfirmer={async (cle) => {
        const p = await api.preparerAcompte(commande.id, { montant: m, ...champsPaiement(ch), date_valeur: date, ...(reference.trim() ? { reference: reference.trim() } : {}) }, cle);
        onFait('Acompte préparé : il attend la signature.', p);
      }}>
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>Commande : {francs(commande.montant)} · acomptes en cours ou versés : {francs(deja)} · reste possible : <strong>{francs(reste)}</strong></p>
      <ChoixCompteMode comptes={comptes} lecture={treso} valeur={choix} onChange={setChoix} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <Champ libelle="Montant (F)" requis><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} /></Champ>
        <Champ libelle="Date de valeur" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
      </div>
      <Champ libelle="Référence" aide="Facultative."><input className="rh-champ-saisie" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} /></Champ>
    </FenetreActe>
  );
}

function FenetreFacture({ commande, organisationId, onFermer, onFait }) {
  const api = useCampagnes();
  const [type, setType] = useState('FACTURE');
  const [numero, setNumero] = useState('');
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(aujourdhuiDakar());
  const piece = usePieceDepot(organisationId, 'COMMANDE', commande.id, 'FACTURE');
  const m = lireMontant(montant);
  return (
    <FenetreActe titre={`Facture ou avoir — commande ${commande.numero}`} Icone={FileText} libelleBouton="Enregistrer" pret={!!(numero.trim() && m && date && piece.prete && piece.saisie.typePiece === type)} onFermer={onFermer}
      aide="La facture arrive après la réception : le net facturé (factures − avoirs) ne peut pas dépasser la quantité reçue × le prix de la commande."
      onConfirmer={async (cle) => {
        const pc = await piece.deposer();
        await api.enregistrerFacture(commande.id, { type, numero_externe: numero.trim(), montant: m, date_facture: date, piece_id: pc.id }, cle);
        onFait(type === 'FACTURE' ? 'Facture enregistrée.' : 'Avoir enregistré.');
      }}>
      <ChoixRadio nom="type-facture" options={{ FACTURE: 'Facture', AVOIR: 'Avoir' }} valeur={type} onChange={(t) => { setType(t); piece.setSaisie({ ...piece.saisie, typePiece: t }); }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <Champ libelle="Numéro du fournisseur" requis><input className="rh-champ-saisie" maxLength={40} value={numero} onChange={(e) => setNumero(e.target.value)} /></Champ>
        <Champ libelle="Montant (F)" requis><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} /></Champ>
        <Champ libelle="Date" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
      </div>
      <ChampPiece piece={piece} types={[type]} libelleChamp={type === 'FACTURE' ? 'Facture (pièce)' : 'Avoir (pièce)'} />
    </FenetreActe>
  );
}

/** Acomptes d'une commande : chaque paiement s'ouvre sur sa fiche (signature, contre-signature, exécution). */
function Acomptes({ commande, organisationId }) {
  const l = toArray(commande.acomptes);
  if (!l.length) return null;
  return (
    <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none', fontSize: 13 }}>
      {l.map((a) => (
        <li key={a.id} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <Link className="rh-lien" to={`/dispositifs/${organisationId}/tresorerie/paiements/${a.id}`}>Acompte {a.numero || '(à numéroter)'}</Link>
          <span>{francs(a.montant)}</span><Etat table={ETATS_PAIEMENT} code={a.statut} />
        </li>
      ))}
    </ul>
  );
}

function Factures({ commande }) {
  const l = toArray(commande.factures);
  if (!l.length) return null;
  return (
    <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none', fontSize: 13 }}>
      {l.map((f) => <li key={f.id}>{f.type === 'AVOIR' ? 'Avoir' : 'Facture'} n° {f.numero_externe} du {dateLongue(String(f.date_facture).slice(0, 10))} : {f.type === 'AVOIR' ? '− ' : ''}{francs(f.montant)}</li>)}
    </ul>
  );
}

export default function AchatsVue({ campagne, droits }) {
  const api = useCampagnes();
  const org = campagne.organisation_id;
  const achats = useLectureSociale(() => api.achats(campagne.id), [api, campagne.id]);
  const four = useLectureSociale(() => api.fournisseurs(org), [api, org]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState(null);
  const fournisseurs = toArray(four.donnees);
  const devis = toArray(achats.donnees?.devis);
  const commandes = toArray(achats.donnees?.commandes);
  const devisMinimum = achats.donnees?.devis_minimum || null;
  const enAchat = ETATS_ACHAT.includes(codeEtat(campagne));
  const fait = (m, paiement) => { setFenetre(null); setInfo({ m, paiement }); achats.recharger(); four.recharger(); };
  const actions = (k) => [
    k.statut === 'BROUILLON' && droits.a('ds_achat_preparer') && { libelle: 'Soumettre pour validation', Icone: Send, onClick: () => setFenetre({ type: 'soumettre', k }) },
    k.statut === 'A_VALIDER' && droits.a('ds_achat_valider') && { libelle: 'Valider', Icone: CheckCircle2, onClick: () => setFenetre({ type: 'decision', k, valider: true }) },
    k.statut === 'A_VALIDER' && droits.a('ds_achat_valider') && { libelle: 'Refuser', Icone: XCircle, onClick: () => setFenetre({ type: 'decision', k, valider: false }) },
    ['VALIDEE', 'PARTIELLEMENT_RECUE'].includes(k.statut) && droits.a('ds_paiement_preparer') && { libelle: 'Préparer un acompte', Icone: Banknote, onClick: () => setFenetre({ type: 'acompte', k }) },
    !['BROUILLON', 'A_VALIDER', 'ANNULEE'].includes(k.statut) && droits.a('ds_facture_enregistrer') && { libelle: 'Enregistrer une facture ou un avoir', Icone: FileText, onClick: () => setFenetre({ type: 'facture', k }) },
    ['BROUILLON', 'A_VALIDER', 'VALIDEE'].includes(k.statut) && droits.a('ds_achat_valider') && { libelle: 'Annuler la commande', Icone: Undo2, danger: true, onClick: () => setFenetre({ type: 'annuler', k }) },
  ].filter(Boolean);
  return (
    <>
      <BandeauComptaDemo />
      {info && (
        <BandeauInfo ton="succes">
          {info.m}{info.paiement?.id && <> <Link className="rh-lien" to={`/dispositifs/${org}/tresorerie/paiements/${info.paiement.id}`}>Ouvrir le paiement</Link></>}
        </BandeauInfo>
      )}
      {!enAchat && <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>Les commandes se préparent une fois la campagne publiée et jusqu'à la fin de la distribution.</p>}

      <section className="rh-carte">
        <TitreSection action={droits.a('ds_achat_preparer') && <Bouton petit Icone={Plus} onClick={() => setFenetre({ type: 'fournisseur' })}>Nouveau fournisseur</Bouton>}>Fournisseurs</TitreSection>
        <EtatsLecture lecture={four} Icone={Building2} estVide={!fournisseurs.length} titreVide="Aucun fournisseur" vide="Enregistrez les fournisseurs consultés : ils servent pour les devis et les commandes de toutes les campagnes de l'organisation.">
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Fournisseur</th><th>NINEA</th><th>Contact</th></tr></thead>
              <tbody>{fournisseurs.map((f) => <tr key={f.id}><td>{f.raison_sociale}{f.actif === false && <Badge ton="neutre">inactif</Badge>}</td><td>{f.ninea || ''}</td><td>{[f.telephone, f.adresse].filter(Boolean).join(' · ')}</td></tr>)}</tbody>
            </table>
          </div>
        </EtatsLecture>
      </section>

      <section className="rh-carte">
        <TitreSection action={droits.a('ds_achat_preparer') && <Bouton petit Icone={Plus} disabled={!fournisseurs.length} onClick={() => setFenetre({ type: 'devis' })}>Enregistrer un devis</Bouton>}>Devis</TitreSection>
        <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>{devis.length} devis enregistré(s). Une commande se valide avec {exigenceDevis(devisMinimum)}{achats.donnees?.devis_minimum_origine === 'DEFAUT_DECIDE' ? ' (défaut décidé, réglable au Centre de configuration)' : ' (règlement de la campagne)'} et le devis retenu motivé, ou avec une dérogation motivée.</p>
        <EtatsLecture lecture={achats} Icone={FileText} estVide={!devis.length} titreVide="Aucun devis" vide="Chaque devis est enregistré avec sa pièce ; il ne se modifie plus ensuite.">
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Fournisseur</th><th>Quantité</th><th style={{ textAlign: 'right' }}>Prix unitaire</th><th style={{ textAlign: 'right' }}>Montant</th><th>Retenu</th></tr></thead>
              <tbody>
                {devis.map((d) => (
                  <tr key={d.id}>
                    <td>{d.raison_sociale}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>saisi le {dateLongue(d.saisi_le)}</span></td>
                    <td>{quantiteUnites(campagne, d.quantite)}</td>
                    <td style={{ textAlign: 'right' }}><Montant n={d.prix_unitaire} /></td>
                    <td style={{ textAlign: 'right' }}><Montant n={d.montant} /></td>
                    <td>{d.retenu ? <Badge ton="succes" point>Retenu</Badge> : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </EtatsLecture>
      </section>

      <section className="rh-carte">
        <TitreSection action={droits.a('ds_achat_preparer') && enAchat && <Bouton petit variante="primaire" Icone={ShoppingCart} disabled={!fournisseurs.length} onClick={() => setFenetre({ type: 'commande' })}>Préparer une commande</Bouton>}>Commandes</TitreSection>
        <EtatsLecture lecture={achats} Icone={ShoppingCart} estVide={!commandes.length} titreVide="Aucune commande" vide="Une commande est préparée par une personne et validée par une autre ; son numéro est attribué à la validation.">
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Commande</th><th>Quantité</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th>Acomptes et factures</th><th aria-label="Actions" /></tr></thead>
              <tbody>
                {commandes.map((k) => {
                  const items = actions(k);
                  return (
                    <tr key={k.id}>
                      <td>
                        <span className="rh-cellule-principale">{k.numero || 'Numéro à la validation'}</span>
                        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{k.raison_sociale}{k.date_livraison ? ` · livraison le ${dateLongue(String(k.date_livraison).slice(0, 10))}` : ''}</span>
                        <span className="rh-cellule-secondaire" style={{ display: 'block', maxWidth: 280 }}>{k.motif_sans_devis ? `Dérogation : ${k.motif_sans_devis}` : k.motif_choix ? `Devis retenu : ${k.motif_choix}` : ''}</span>
                        {(k.prepare_par_nom || k.valide_par_nom) && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{k.prepare_par_nom ? `préparée par ${k.prepare_par_nom}` : ''}{k.valide_par_nom ? `${k.prepare_par_nom ? ', ' : ''}validée par ${k.valide_par_nom}` : ''}</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{quantiteUnites(campagne, k.quantite)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>à {francs(k.prix_unitaire)}</span></td>
                      <td style={{ textAlign: 'right' }}><Montant n={k.montant} fort /></td>
                      <td>
                        <Etat table={STATUTS_COMMANDE} code={k.statut} />
                        {toArray(k.receptions).length > 0 && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{toArray(k.receptions).length} réception(s)</span>}
                        {k.motif_annulation && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{k.motif_annulation}</span>}
                      </td>
                      <td><Acomptes commande={k} organisationId={org} /><Factures commande={k} />{!toArray(k.acomptes).length && !toArray(k.factures).length && <span className="rh-cellule-secondaire">—</span>}</td>
                      <td style={{ textAlign: 'right' }}>{items.length > 0 && <MenuActions iconeSeule gauche Icone={MoreVertical} libelle={`Actions sur la commande ${k.numero || ''}`} items={items} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </EtatsLecture>
      </section>

      {fenetre?.type === 'fournisseur' && <FenetreFournisseur organisationId={org} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'devis' && <FenetreDevis campagne={campagne} fournisseurs={fournisseurs} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'commande' && <FenetreCommande campagne={campagne} fournisseurs={fournisseurs} devis={devis} devisMinimum={devisMinimum} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'soumettre' && (
        <FenetreActe titre="Soumettre la commande" Icone={Send} libelleBouton="Soumettre" onFermer={() => setFenetre(null)}
          aide={`Le serveur contrôle ${exigenceDevis(devisMinimum)} et le devis retenu motivé, ou la dérogation motivée. Une autre personne validera.`}
          onConfirmer={async () => { await api.soumettreCommande(fenetre.k.id); fait('Commande soumise : elle attend la validation d\'une autre personne.'); }}>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>{fenetre.k.raison_sociale} — {quantiteUnites(campagne, fenetre.k.quantite)} pour {francs(fenetre.k.montant)}</p>
        </FenetreActe>
      )}
      {fenetre?.type === 'decision' && <FenetreDecision commande={fenetre.k} valider={fenetre.valider} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'annuler' && <FenetreAnnulerCommande commande={fenetre.k} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'acompte' && <FenetreAcompte commande={fenetre.k} organisationId={org} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'facture' && <FenetreFacture commande={fenetre.k} organisationId={org} onFermer={() => setFenetre(null)} onFait={fait} />}
    </>
  );
}
